import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormHelperText,
  FormControlLabel,
  IconButton,
  Grid,
  MenuItem,
  TextField,
  Tooltip,
  Stack,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';

import ActionSettingsLayout from '../ActionSettingsHeader';
import TagEditor from '../Widgets/TagEditor';
import { useActionSettings } from '../../hooks/useActionSettings';
import { useFolderPicker } from '../useFolderPicker';
import { useFolderRestrictions } from '../useFolderRestrictions';

const createRowValueCell = (value = '', keepExisting = false, id = null) => ({
  id: id || `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
  value,
  keepExisting
});

const normalizeRowValueCells = (rowValuesRaw) => {
  if (rowValuesRaw === null || rowValuesRaw === undefined) {
    return [];
  }

  if (Array.isArray(rowValuesRaw)) {
    return rowValuesRaw.map((value) => {
      if (typeof value === 'string') {
        return createRowValueCell(value, false);
      }

      if (value && typeof value === 'object') {
        return createRowValueCell(
          value.value ?? value.text ?? value.rowValue ?? value.cellValue ?? '',
          Boolean(value.keepExisting ?? value.keep_existing ?? value.keepCurrent),
          value.id ?? null
        );
      }

      return createRowValueCell(String(value ?? ''), false);
    });
  }

  if (typeof rowValuesRaw === 'object') {
    return [createRowValueCell(
      rowValuesRaw.value ?? rowValuesRaw.text ?? rowValuesRaw.rowValue ?? rowValuesRaw.cellValue ?? '',
      Boolean(rowValuesRaw.keepExisting ?? rowValuesRaw.keep_existing ?? rowValuesRaw.keepCurrent),
      rowValuesRaw.id ?? null
    )];
  }

  const text = String(rowValuesRaw || '').trim();
  if (!text) {
    return [];
  }

  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return normalizeRowValueCells(parsed);
    }
  } catch (error) {
    // Fall back to CSV parsing below.
  }

  return text.split(',').map((value) => createRowValueCell(value.trim(), false));
};

const serializeRowValueCells = (cells) => cells.map((cell) => ({
  value: cell.keepExisting ? '' : String(cell.value ?? ''),
  keepExisting: Boolean(cell.keepExisting)
}));

const serializeRowValueCellsToText = (cells) => cells
  .map((cell) => (cell.keepExisting ? '' : String(cell.value ?? '')))
  .join(',');

const compactRowValueCells = (cells) => cells.filter((cell) => (
  Boolean(cell.keepExisting) || String(cell.value ?? '').trim().length > 0
));

const getExcelColumnLabel = (index) => {
  let value = index + 1;
  let label = '';

  while (value > 0) {
    const remainder = (value - 1) % 26;
    label = String.fromCharCode(65 + remainder) + label;
    value = Math.floor((value - 1) / 26);
  }

  return label;
};

const SectionTitle = ({ label, tooltip }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
    <Typography component="span" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 500, lineHeight: 1.2 }}>
      {label}
    </Typography>
    {tooltip ? (
      <Tooltip title={tooltip} arrow>
        <InfoOutlinedIcon fontSize="small" color="action" sx={{ cursor: 'help' }} />
      </Tooltip>
    ) : null}
  </Box>
);

const ActionsAddExcelRow = ({ caseid, tags, settingId, onSaveStatusChange, initialScope = 'event' }) => {
  const { t } = useTranslation();

  const [sourcePath, setSourcePath] = useState('');
  const [targetPath, setTargetPath] = useState('/{filename}_with_row.xlsx');
  const [sheetName, setSheetName] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchColumn, setSearchColumn] = useState('1');
  const [searchMode, setSearchMode] = useState('contains');
  const [addIfNotFound, setAddIfNotFound] = useState(true);
  const [addIfNotFoundMode, setAddIfNotFoundMode] = useState('append_end');
  const [rowValueCells, setRowValueCells] = useState(() => [createRowValueCell('')]);
  const [insertAtRow, setInsertAtRow] = useState('');
  const [startColumn, setStartColumn] = useState('1');
  const [sourceBrowsingDisabled, setSourceBrowsingDisabled] = useState(false);
  const [targetBrowsingDisabled, setTargetBrowsingDisabled] = useState(false);

  const { restrictToUserFolder, allowedRoots, displayRoot } = useFolderRestrictions(caseid);

  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow,
    updateSaveDisabledState
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'addexcelrow',
    onSaveStatusChange,
    initialScope,
    customComparison: (initial = {}) => (
      sourcePath === (initial.sourceFilePath || '') &&
      targetPath === (initial.target || initial.targetPath || '') &&
      sheetName === (initial.sheetName || '') &&
      searchKeyword === (initial.searchKeyword || initial.matchKeyword || '') &&
      searchColumn === String(initial.searchColumn || initial.searchColumnIndex || initial.matchColumn || '1') &&
      searchMode === String(initial.searchMode || initial.matchMode || 'contains') &&
      addIfNotFound === (initial.addIfNotFound !== undefined ? Boolean(initial.addIfNotFound) : true) &&
      addIfNotFoundMode === String(initial.addIfNotFoundMode || initial.addIfNotFoundPosition || 'append_end') &&
      insertAtRow === String(initial.insertAtRow || initial.rowIndex || '') &&
      startColumn === String(initial.startColumn || initial.valueColumnIndex || '1') &&
      JSON.stringify(serializeRowValueCells(compactRowValueCells(rowValueCells))) === JSON.stringify(
        serializeRowValueCells(compactRowValueCells(normalizeRowValueCells(initial.rowValueRows || initial.rowValuesRows || initial.rowValues || '')))
      )
    ),
    customSaveData: () => ({
      sourceFilePath: sourcePath,
      target: targetPath,
      sheetName,
      searchKeyword,
      searchColumn: searchColumn ? String(searchColumn) : null,
      searchMode,
      addIfNotFound,
      addIfNotFoundMode: addIfNotFound ? addIfNotFoundMode : 'append_end',
      insertAtRow: insertAtRow ? Number(insertAtRow) : null,
      startColumn: startColumn ? Number(startColumn) : null,
      rowValueRows: serializeRowValueCells(compactRowValueCells(rowValueCells)),
      rowValues: serializeRowValueCellsToText(compactRowValueCells(rowValueCells))
    }),
    loadDataFunction: (data) => {
      const customData = data.customData || {};
      setSourcePath(customData.sourceFilePath || customData.sourcePath || '');
      setTargetPath(customData.target || customData.targetPath || '/{filename}_with_row.xlsx');
      setSheetName(customData.sheetName || '');
      setSearchKeyword(customData.searchKeyword || customData.matchKeyword || '');
      setSearchColumn(customData.searchColumn || customData.searchColumnIndex || customData.matchColumn ? String(customData.searchColumn || customData.searchColumnIndex || customData.matchColumn) : '1');
      setSearchMode((customData.searchMode || customData.matchMode || 'contains').toString());
      setAddIfNotFound(customData.addIfNotFound !== undefined ? Boolean(customData.addIfNotFound) : true);
      setAddIfNotFoundMode((customData.addIfNotFoundMode || customData.addIfNotFoundPosition || 'append_end').toString());
      setInsertAtRow(customData.insertAtRow ? String(customData.insertAtRow) : '');
      setStartColumn(customData.startColumn || customData.valueColumnIndex ? String(customData.startColumn || customData.valueColumnIndex) : '1');
      const normalizedRowValueCells = normalizeRowValueCells(customData.rowValueRows || customData.rowValuesRows || customData.rowValues || '');
      setRowValueCells(normalizedRowValueCells.length > 0 ? normalizedRowValueCells : [createRowValueCell('')]);
    }
  });

  const { handleBrowseDirectory: handleBrowseSourceFile, FolderBrowserComponent: SourceFileBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    allowFileSelection: true,
    onFolderSelected: (filePath) => {
      if (filePath) {
        setSourcePath(filePath);
      }
      setSourceBrowsingDisabled(false);
    },
    onFolderBrowserClose: () => {
      setSourceBrowsingDisabled(false);
    }
  });

  const { handleBrowseDirectory: handleBrowseTargetDirectory, FolderBrowserComponent: TargetFolderBrowserComponent } = useFolderPicker({
    restrictToUserFolder,
    allowedRoots,
    displayRoot,
    onFolderSelected: (folderPath) => {
      if (folderPath) {
        const folderPathClean = folderPath.replace(/[\\/]+$/, '');
        const separator = folderPathClean.includes('\\') ? '\\' : '/';
        setTargetPath(`${folderPathClean}${separator}{filename}_with_row.xlsx`);
      }
      setTargetBrowsingDisabled(false);
    },
    onFolderBrowserClose: () => {
      setTargetBrowsingDisabled(false);
    }
  });

  useEffect(() => {
    updateSaveDisabledState();
  }, [sourcePath, targetPath, sheetName, searchKeyword, searchColumn, searchMode, addIfNotFound, addIfNotFoundMode, rowValueCells, insertAtRow, startColumn, updateSaveDisabledState]);

  const validation = useMemo(() => {
    const hasValue = rowValueCells.some((cell) => String(cell.value ?? '').trim() || cell.keepExisting);
    if (!hasValue) {
      return {
        error: true,
        helper: t('workflow.add_excel_row.row_required', 'Row values are required')
      };
    }
    if (!targetPath.trim()) {
      return {
        error: true,
        helper: t('workflow.add_excel_row.target_required', 'Target path is required')
      };
    }
    return { error: false, helper: '' };
  }, [rowValueCells, targetPath, t]);

  const customDataTags = [
    {
      key: 'filename',
      label: 'Filename',
      value: 'filename',
      description: 'Source filename without extension'
    },
    {
      key: 'file_extension',
      label: 'File Extension',
      value: 'file_extension',
      description: 'Source file extension without the dot'
    },
    {
      key: 'file_folder_path',
      label: 'File Folder Path',
      value: 'file_folder_path',
      description: 'Folder path of the current source file'
    }
  ];

  const rowPreviewValue = searchKeyword?.trim()
    ? searchKeyword
    : t('workflow.add_excel_row.matched_cell_placeholder', 'Search value will appear here');

  return (
    <ActionSettingsLayout
      title={t('addexcelrow', 'Add Excel Row')}
      isEnabled={isEnabled}
      onEnabledChange={setIsEnabled}
      isSaveDisabled={isSaveDisabled || validation.error}
      onSave={handleSave}
      onForceRun={handleForceRun}
      forceRunLoading={forceRunLoading}
      forceRunResult={forceRunResult}
      popUpOpen={popUpOpen}
      onCloseStatusWindow={handleCloseStatusWindow}
      forceRunWarningOpen={forceRunWarningOpen}
      forceRunWarningCount={forceRunWarningCount}
      onForceRunConfirm={handleForceRunConfirm}
      onForceRunCancel={handleForceRunCancel}
      conditions={conditions}
      onConditionsChange={handleConditionsChange}
      tags={tags}
      customDataTags={customDataTags}
    >
      <Grid item xs={12}>
        <Card sx={{ mb: 2 }}>
          <CardHeader
            title={(
              <SectionTitle
                label={t('workflow.add_excel_row.file_section_title', 'Files and sheet')}
                tooltip={t('workflow.add_excel_row.file_section_tooltip', 'Choose the file to read, the file to save, and the sheet to use.')}
              />
            )}
            sx={{ p: { xs: 1.5, sm: 2 } }}
          />
          <CardContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t(
                'workflow.add_excel_row.file_section_description',
                'Choose the file to read, the file to save, and the sheet to use.'
              )}
            </Typography>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ width: { xs: 74, sm: 110 }, flexShrink: 0, lineHeight: 1.1 }}
                >
                  {t('workflow.add_excel_row.source_label', 'Source file path')}
                </Typography>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <TagEditor
                    caseid={caseid}
                    value={sourcePath}
                    onChange={setSourcePath}
                    placeholder={t('workflow.add_excel_row.source_placeholder', 'Leave blank to use the current event workbook')}
                    singleLine
                    tags={tags}
                  />
                </Box>
                <Tooltip title={t('workflow.add_excel_row.choose_file_button', 'Choose file')} arrow>
                  <span>
                    <IconButton
                      onClick={() => {
                        setSourceBrowsingDisabled(true);
                        handleBrowseSourceFile();
                      }}
                      disabled={sourceBrowsingDisabled}
                      aria-label={t('workflow.add_excel_row.choose_file_button', 'Choose file')}
                      size="small"
                    >
                      <FolderOpenOutlinedIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ width: { xs: 74, sm: 110 }, flexShrink: 0, lineHeight: 1.1 }}
                >
                  {t('workflow.add_excel_row.target_label', 'Target path')}
                </Typography>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <TagEditor
                    caseid={caseid}
                    value={targetPath}
                    onChange={setTargetPath}
                    placeholder={t('workflow.add_excel_row.target_placeholder', '/{filename}_with_row.xlsx')}
                    singleLine
                    tags={tags}
                  />
                </Box>
                <Tooltip title={t('workflow.add_excel_row.choose_folder_button', 'Choose folder')} arrow>
                  <span>
                    <IconButton
                      onClick={() => {
                        setTargetBrowsingDisabled(true);
                        handleBrowseTargetDirectory();
                      }}
                      disabled={targetBrowsingDisabled}
                      aria-label={t('workflow.add_excel_row.choose_folder_button', 'Choose folder')}
                      size="small"
                    >
                      <FolderIcon sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ width: { xs: 74, sm: 110 }, flexShrink: 0, lineHeight: 1.1 }}
                >
                  {t('workflow.add_excel_row.sheet_label', 'Sheet name')}
                </Typography>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <TagEditor
                    caseid={caseid}
                    value={sheetName}
                    onChange={setSheetName}
                    placeholder={t('workflow.add_excel_row.sheet_placeholder', 'Sheet1')}
                    singleLine
                    tags={tags}
                  />
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ mb: 2 }}>
          <CardHeader
            title={(
              <SectionTitle
                label={t('workflow.add_excel_row.match_title', 'Find row')}
                tooltip={t('workflow.add_excel_row.match_title_tooltip', 'We compare the text you enter with the cells in one column. When we find a match, the new values start to the right of it.')}
              />
            )}
            sx={{ p: { xs: 1.5, sm: 2 } }}
          />
          <CardContent>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                <Typography variant="caption" color="textSecondary" sx={{ width: { xs: 74, sm: 110 }, flexShrink: 0, lineHeight: 1.1 }}>
                  {t('workflow.add_excel_row.search_keyword_label', 'Text to find')}
                </Typography>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <TagEditor
                    caseid={caseid}
                    value={searchKeyword}
                    onChange={setSearchKeyword}
                    placeholder={t('workflow.add_excel_row.search_keyword_placeholder', 'status')}
                    singleLine
                    dense
                    minHeight="32px"
                    maxRows={1}
                    tags={tags}
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
                <Typography variant="caption" color="textSecondary" sx={{ width: { xs: 74, sm: 110 }, flexShrink: 0, lineHeight: 1.1 }}>
                  {t('workflow.add_excel_row.search_column_label', 'Column to check')}
                </Typography>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <TagEditor
                    caseid={caseid}
                    value={searchColumn}
                    onChange={setSearchColumn}
                    placeholder={t('workflow.add_excel_row.search_column_placeholder', '1')}
                    singleLine
                    dense
                    minHeight="32px"
                    maxRows={1}
                    tags={tags}
                  />
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                  {t('workflow.add_excel_row.search_mode_label', 'How to match')}
                </Typography>
                <TextField
                  fullWidth
                  select
                  value={searchMode}
                  onChange={(event) => setSearchMode(event.target.value)}
                  helperText={t('workflow.add_excel_row.search_mode_helper', 'Contains means the text can be inside a cell. Exact match means the cell must match it exactly.')}
                >
                  <MenuItem value="contains">{t('workflow.add_excel_row.search_mode_contains', 'Contains')}</MenuItem>
                  <MenuItem value="exact">{t('workflow.add_excel_row.search_mode_exact', 'Exact match')}</MenuItem>
                  <MenuItem value="starts_with">{t('workflow.add_excel_row.search_mode_starts_with', 'Starts with')}</MenuItem>
                </TextField>
              </Box>

              <Box>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={addIfNotFound}
                      onChange={(event) => setAddIfNotFound(event.target.checked)}
                      color="primary"
                      size="small"
                    />
                  )}
                  label={t(
                    'workflow.add_excel_row.add_new_row_if_not_found_label',
                    'Add new row if not found'
                  )}
                />
                {addIfNotFound ? (
                  <Box sx={{ pl: 4.2, pr: 0.5, mt: 0.5 }}>
                    <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                      {t('workflow.add_excel_row.add_new_row_mode_label', 'When not found')}
                    </Typography>
                    <TextField
                      fullWidth
                      select
                      value={addIfNotFoundMode}
                      onChange={(event) => setAddIfNotFoundMode(event.target.value)}
                      helperText={t(
                        'workflow.add_excel_row.add_new_row_mode_helper',
                        'Choose whether the missing row is appended at the end or inserted by order.'
                      )}
                    >
                      <MenuItem value="append_end">
                        {t('workflow.add_excel_row.add_new_row_mode_append_end', 'Add at the end')}
                      </MenuItem>
                      <MenuItem value="insert_by_order">
                        {t(
                          'workflow.add_excel_row.add_new_row_mode_insert_by_order',
                          'Add by order starting from the first element'
                        )}
                      </MenuItem>
                    </TextField>
                  </Box>
                ) : null}
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ mb: 2 }}>
          <CardHeader
            title={t('workflow.add_excel_row.row_title', 'New row')}
            titleTypographyProps={{
              sx: { fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 500 }
            }}
            sx={{ p: { xs: 1.5, sm: 2 } }}
          />
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'workflow.add_excel_row.row_values_description',
                  'Enter one value per cell. Use tags if you need dynamic text.'
                )}
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  flexDirection: 'column',
                  minWidth: 'fit-content',
                  overflowX: 'auto',
                  pb: 0.5,
                  pt: 0.5,
                  pr: 0.5,
                  backgroundImage: 'linear-gradient(180deg, rgba(243,246,243,0.95), rgba(255,255,255,0.98))',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 0.5,
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                  <Box
                    sx={{
                      minWidth: 188,
                      flex: '0 0 188px',
                      px: 1,
                      py: 0.6,
                      minHeight: 30,
                      backgroundColor: '#e8eee8',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRight: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: '0.08em' }}>
                      {t('workflow.add_excel_row.matched_cell_header', 'A')}
                    </Typography>
                  </Box>
                  {rowValueCells.map((cell, index) => {
                    const columnLabel = getExcelColumnLabel(index + 1);

                    return (
                      <Box
                        key={`header_${cell.id}`}
                        sx={{
                          minWidth: 188,
                          flex: '0 0 188px',
                          px: 1,
                          py: 0.6,
                          minHeight: 30,
                          backgroundColor: '#e8eee8',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRight: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: '0.08em' }}>
                          {columnLabel}
                        </Typography>
                      </Box>
                    );
                  })}
                  <Box
                    sx={{
                      minWidth: 84,
                      flex: '0 0 84px',
                      px: 1,
                      py: 0.6,
                      minHeight: 30,
                      backgroundColor: '#e8eee8',
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: '0.08em' }}>
                      +
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                  <Box
                    sx={{
                      minWidth: 188,
                      flex: '0 0 188px',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderTop: 'none',
                      borderRight: 'none',
                      backgroundColor: '#f8faf8',
                      position: 'relative',
                      minHeight: 92,
                      px: 1,
                      py: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                      <LockOutlinedIcon fontSize="small" color="primary" />
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {t('workflow.add_excel_row.search_keyword_label', 'Text to find')}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: '#fff',
                        minHeight: 34,
                        display: 'flex',
                        alignItems: 'center',
                        px: 1.1,
                        py: 0.7,
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)'
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {rowPreviewValue}
                      </Typography>
                    </Box>
                  </Box>
                  {rowValueCells.map((cell, index) => (
                    <Box
                      key={cell.id}
                      sx={{
                        minWidth: 188,
                        flex: '0 0 188px',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderTop: 'none',
                        borderRight: 'none',
                        backgroundColor: '#fff',
                        position: 'relative',
                        minHeight: 92,
                        px: 1,
                        py: 1
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.25
                        }}
                      >
                        <Tooltip
                          title={cell.keepExisting
                            ? t('workflow.add_excel_row.keep_existing_enabled', 'Keep current cell value')
                            : t('workflow.add_excel_row.keep_existing_disabled', 'Write a new value')}
                          arrow
                        >
                          <IconButton
                            size="small"
                            onClick={() => setRowValueCells((current) => current.map((currentCell, currentIndex) => (
                              currentIndex === index
                                ? { ...currentCell, keepExisting: !currentCell.keepExisting }
                                : currentCell
                            )))}
                            sx={{ p: 0.15 }}
                            aria-label={cell.keepExisting
                              ? t('workflow.add_excel_row.keep_existing_enabled', 'Keep current cell value')
                              : t('workflow.add_excel_row.keep_existing_disabled', 'Write a new value')}
                          >
                            {cell.keepExisting ? (
                              <LockOutlinedIcon fontSize="inherit" color="primary" />
                            ) : (
                              <EditOutlinedIcon fontSize="inherit" color="action" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('workflow.add_excel_row.remove_cell_button', 'Remove cell')} arrow>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => setRowValueCells((current) => {
                                if (current.length <= 1) {
                                  return [createRowValueCell('')];
                                }
                                return current.filter((_, currentIndex) => currentIndex !== index);
                              })}
                              sx={{ p: 0.15 }}
                              aria-label={t('workflow.add_excel_row.remove_cell_button', 'Remove cell')}
                              disabled={rowValueCells.length <= 1}
                            >
                              <DeleteOutlineIcon fontSize="inherit" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      {!cell.keepExisting ? (
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: '#fff',
                            minHeight: 34,
                            display: 'flex',
                            alignItems: 'stretch',
                            mt: 2.5,
                            '&:focus-within': {
                              borderColor: 'primary.main',
                              boxShadow: 'inset 0 0 0 1px rgba(25, 118, 210, 0.12)'
                            }
                          }}
                        >
                          <TagEditor
                            caseid={caseid}
                            value={cell.value}
                            onChange={(nextValue) => setRowValueCells((current) => current.map((currentCell, currentIndex) => (
                              currentIndex === index ? { ...currentCell, value: nextValue } : currentCell
                            )))}
                            placeholder={t('workflow.add_excel_row.row_cell_placeholder', 'Type a value')}
                            singleLine
                            dense
                            minHeight="32px"
                            maxRows={1}
                            tags={tags}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            mt: 2.5,
                            minHeight: 34,
                            border: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: '#f8faf8',
                            display: 'flex',
                            alignItems: 'center',
                            px: 1.1,
                            color: 'text.secondary',
                            fontStyle: 'italic',
                            fontSize: '0.875rem'
                          }}
                        >
                          {t('workflow.add_excel_row.keep_existing_label', 'Keep current value')}
                        </Box>
                      )}
                    </Box>
                  ))}
                  <Box
                    sx={{
                      minWidth: 84,
                      flex: '0 0 84px',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderTop: 'none',
                      backgroundColor: '#f8faf8',
                      minHeight: 92,
                      px: 1,
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Tooltip title={t('workflow.add_excel_row.add_cell_button', 'Add cell')} arrow>
                      <IconButton
                        onClick={() => setRowValueCells((current) => [...current, createRowValueCell('')])}
                        aria-label={t('workflow.add_excel_row.add_cell_button', 'Add cell')}
                        sx={{
                          border: '1px dashed',
                          borderColor: 'divider',
                          borderRadius: 0,
                          px: 1.25,
                          minWidth: 48,
                          backgroundColor: '#fff',
                          '&:hover': {
                            backgroundColor: '#fbfdfb'
                          }
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
              <FormHelperText error={validation.error}>
                {validation.error ? validation.helper : t(
                  'workflow.add_excel_row.row_helper',
                  'Enter one value per cell, separated by commas. Tags can be used inside each value.'
                )}
              </FormHelperText>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ mb: 2 }}>
          <CardHeader
            title={t('workflow.add_excel_row.position_title', 'Row position')}
            titleTypographyProps={{
              sx: { fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 500 }
            }}
            sx={{ p: { xs: 1.5, sm: 2 } }}
          />
          <CardContent>
            <Stack spacing={2}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                    {t('workflow.add_excel_row.insert_at_label', 'Insert at row')}
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={insertAtRow}
                    onChange={(event) => setInsertAtRow(event.target.value)}
                    placeholder={t('workflow.add_excel_row.insert_at_placeholder', 'Leave blank to append at the end')}
                    inputProps={{ min: 1 }}
                  />
                  <FormHelperText>
                    {t('workflow.add_excel_row.insert_at_helper', 'Use 1-based Excel row numbering.')}
                  </FormHelperText>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>
                    {t('workflow.add_excel_row.start_column_label', 'Start column')}
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={startColumn}
                    onChange={(event) => setStartColumn(event.target.value)}
                    placeholder={t('workflow.add_excel_row.start_column_placeholder', '1')}
                    inputProps={{ min: 1 }}
                  />
                  <FormHelperText>
                    {t('workflow.add_excel_row.start_column_helper', 'The column index where values will start being inserted.')}
                  </FormHelperText>
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {SourceFileBrowserComponent}
      {TargetFolderBrowserComponent}
    </ActionSettingsLayout>
  );
};

export default ActionsAddExcelRow;
